/**
 * KnobsPanel — Scrutiny / Research Depth / India Lens knobs
 * Authority: DEAL_EVALUATOR_PAGE_SPEC.md Section 5.6 (knobs footer)
 * Design: DS-CARD-V5 ACTION_BUTTONS.knobStates tokens — zero inline hex.
 *
 * Props:
 *   - values   : { scrutiny: 0|1|2, research_depth: 0|1|2, india_lens: 0|1|2 }
 *   - onChange  : (key, value) => void
 *   - disabled  : bool
 */

import React from 'react';
import { ACTION_BUTTONS, DS_TYPOGRAPHY, DS_COLORS } from '@/config/ui-invariants';
import modifiersConfig from '@/config/deal-screen-modifiers.json';

// WI-DS-UI-003: Knob labels sourced from deal-screen-modifiers.json — single authority
const KNOBS = Object.entries(modifiersConfig.modifiers).map(([key, def]) => ({
  key: key.replace('_level', ''), // scrutiny_level → scrutiny (matches backend modifier keys)
  label: def.label,
  levels: Object.entries(def.levels).map(([val, info]) => ({
    value: parseInt(val, 10),
    label: info.label,
  })),
}));

export default function KnobsPanel({ values = {}, onChange, disabled }) {
  const states = ACTION_BUTTONS.knobStates;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
      {KNOBS.map((knob) => {
        const currentValue = values[knob.key] ?? 0;
        return (
          <div key={knob.key} style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <span style={{
              ...DS_TYPOGRAPHY.selectorBtn,
              color: DS_COLORS.text.secondary,
              minWidth: '110px',
              textAlign: 'right',
            }}>
              {knob.label}
            </span>
            <div style={{ display: 'flex', gap: '6px' }}>
              {knob.levels.map((level) => {
                const isActive = currentValue === level.value;
                const stateTokens = isActive ? states[1] : states[0];
                return (
                  <button
                    key={level.value}
                    type="button"
                    onClick={() => onChange?.(knob.key, level.value)}
                    disabled={disabled}
                    onMouseEnter={(e) => {
                      if (!disabled && !isActive) {
                        e.currentTarget.style.borderColor = ACTION_BUTTONS.hover.border;
                        e.currentTarget.style.color = ACTION_BUTTONS.hover.text;
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!disabled && !isActive) {
                        e.currentTarget.style.borderColor = stateTokens.border;
                        e.currentTarget.style.color = stateTokens.color;
                      }
                    }}
                    style={{
                      background: stateTokens.background,
                      color: stateTokens.color,
                      border: `1.5px solid ${stateTokens.border}`,
                      borderRadius: '8px',
                      minHeight: '44px',
                      padding: '6px 14px',
                      ...DS_TYPOGRAPHY.selectorBtn,
                      cursor: disabled ? 'not-allowed' : 'pointer',
                      opacity: disabled ? 0.5 : 1,
                      transition: 'all 0.15s ease',
                    }}
                  >
                    {level.label}
                  </button>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}
